import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { type ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import {
  ManagerProgressionTaskState,
  TaskLineupType,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Refresh } from '@sorare/core/src/atoms/icons/Refresh';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { ArrowDownShape as ArrowDownShapeSvg } from '@sorare/core/src/atoms/ui/ArrowDownShape/index.tsx';
import { JonumKycPromptDialog } from '@sorare/core/src/components/jonum/JonumVerificationDialog/JonumKycPromptDialog';
import {
  getKycClaimState,
  shouldFillKycBeforeClaim as getShouldFillKycBeforeClaim,
  useJonumVerificationGuard,
} from '@sorare/core/src/components/jonum/JonumVerificationDialog/useJonumVerificationGuard';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { ANY_SPORT_PLAY_BOARDS_PRO } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { isType } from '@sorare/core/src/lib/gql/index.ts';

import { Lifes } from '#src/components/boards/Lifes/index.tsx';
import { ReplayIntroDialog } from '#src/components/series/ReplayIntroDialog/index.tsx';
import { SeriesBoardOnboarding } from '#src/components/series/SeriesBoard/SeriesBoardOnboarding/index.tsx';
import { TrainingSetDialog } from '#src/components/series/TrainingSetDialog/index.tsx';

import { ActionRequiredCta } from './ActionRequiredCta/index.tsx';
import { CheckmarkTile } from './CheckmarkTile/index.tsx';
import { ClaimableTile } from './ClaimableTile/index.tsx';
import { FailedTile } from './FailedTile/index.tsx';
import { PendingVerificationCta } from './PendingVerificationCta/index.tsx';
import { ReadyAndSelectedTile } from './ReadyAndSelectedTile/index.tsx';
import { ReadyIcon, ReadyIconWrapper } from './ReadyIcon/index.tsx';
import { ReadyTile } from './ReadyTile/index.tsx';
import { TaskTooltipContent } from './TaskTooltipContent/index.tsx';
import type { HotStreakTile_thresholdPickerTask } from './__generated__/index.graphql.ts';
import lockedTile from './assets/lockedTile.webp';
import {
  CroppedPlayerImage,
  Dot,
  greenOverlay,
  LiveChip,
  orangeOverlay,
  redOverlay,
  TileWrapper,
} from './ui.tsx';

const StyledButtonBase = styled(ButtonBase)`
  z-index: 1;
`;

const ActionRequiredTile = styled.div`
  position: relative;
  display: inline-flex;
`;

const ActionRequiredCtaWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
`;

const UnderlineLink = styled(Link)`
  text-decoration: underline;
`;

type Props = {
  task: HotStreakTile_thresholdPickerTask;
  showOnboarding?: boolean;
  onClaimTask: (id: string) => void;
  onOpenHotStreaksTutorialStory?: () => void;
  showTargetValue?: boolean;
  setShowTargetValue?: (showTargetValue: boolean) => void;
  lineupsPath: string;
  composePath: string;
  oldThreshold?: number | null;
  isReplay?: boolean;
};
export const HotStreakTile = ({
  task,
  showOnboarding = false,
  onClaimTask,
  onOpenHotStreaksTutorialStory,
  showTargetValue = false,
  setShowTargetValue,
  lineupsPath,
  composePath,
  oldThreshold = null,
  isReplay = false,
}: Props) => {
  const navigate = useNavigate();
  const { lifecycle } = useLifecycle();
  const { generateSportContextPath } = useSportContext();
  const { kycRequired, kycUnderReview } = useJonumVerificationGuard();
  const [openReplayIntroDialog, setOpenReplayIntroDialog] = useState(false);
  const [openKycPromptDialog, setOpenKycPromptDialog] = useState(false);
  const {
    id,
    aasmState,
    rewardConfigs,
    replay,
    averageMode,
    displayedPlayer,
    taskLineups,
    parentTask,
  } = task;

  const shouldFillKycBeforeClaim = getShouldFillKycBeforeClaim(
    kycRequired,
    rewardConfigs
  );

  const tooltipContent = (
    <TaskTooltipContent
      task={task}
      showTargetValue={showTargetValue}
      oldThreshold={oldThreshold}
    />
  );

  const hasSharedPool = rewardConfigs.some(
    rc => isType(rc, 'MonetaryRewardConfig') && rc.sharedPool
  );

  const isParentTrackCancelled =
    isType(parentTask, 'TasksTrack') &&
    parentTask.aasmState === ManagerProgressionTaskState.CANCELED;

  const hasSubmittedLineups = taskLineups.length > 0;
  const hasFailedAllSubmittedLineups =
    hasSubmittedLineups &&
    taskLineups.every(lineup => lineup.aasmState === TaskLineupType.FAILED);

  const hasFailedReplay =
    replay &&
    aasmState === ManagerProgressionTaskState.READY &&
    hasFailedAllSubmittedLineups;

  const to = replay ? composePath : lineupsPath;

  if (
    hasFailedReplay ||
    task.aasmState === ManagerProgressionTaskState.CANCELED
  ) {
    return (
      <StyledButtonBase to={to}>
        <TileWrapper>
          <FailedTile />
          <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
            <Vertical center>
              <Refresh />
              <BrandS>
                <FormattedMessage defaultMessage="Miss" />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );
  }

  if (aasmState === ManagerProgressionTaskState.READY) {
    if (hasSubmittedLineups) {
      if (hasFailedAllSubmittedLineups) {
        return (
          <StyledButtonBase to={lineupsPath}>
            <TileWrapper>
              <FailedTile />
              <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
                <CroppedPlayerImage
                  style={{
                    background: `${redOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                  }}
                />
                <LiveChip color="black">
                  <Lifes task={task} size={1} />
                </LiveChip>
              </ArrowDownShapeSvg>
            </TileWrapper>
          </StyledButtonBase>
        );
      }

      const hasLiveGames = task.taskLineups.some(lineup => lineup.hasLiveGames);
      return (
        <StyledButtonBase to={lineupsPath}>
          <TileWrapper>
            <ReadyAndSelectedTile />
            <ArrowDownShapeSvg
              animatedOnHover
              colors={{ start: '#FEF314', end: '#FF4E22' }}
            >
              <CroppedPlayerImage
                style={{
                  background: `${orangeOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                }}
              />
              {!!hasLiveGames && (
                <LiveChip color="black">
                  <Dot />
                  <BrandXXS>
                    <FormattedMessage defaultMessage="Live" />
                  </BrandXXS>
                </LiveChip>
              )}
            </ArrowDownShapeSvg>
          </TileWrapper>
        </StyledButtonBase>
      );
    }

    const onClick = () => {
      if (replay) {
        setOpenReplayIntroDialog(true);
        return;
      }
      if (!lifecycle?.sawSeriesHotStreakOnboarding) {
        onOpenHotStreaksTutorialStory?.();
        return;
      }
      navigate(to);
    };

    return (
      <>
        <OnboardingHighlightWrapper
          highlightName="SeriesBoardReplayButton"
          wrap={!!showOnboarding}
        >
          <ButtonBase
            onClick={() => {
              if (replay) {
                setOpenReplayIntroDialog(true);
                return;
              }
              if (
                !lifecycle?.sawSeriesHotStreakOnboarding &&
                onOpenHotStreaksTutorialStory
              ) {
                onOpenHotStreaksTutorialStory?.();
                return;
              }
              navigate(to);
            }}
          >
            <TileWrapper>
              <ReadyTile />
              <ReadyIconWrapper className="animated">
                <Tooltip
                  open
                  placement="top"
                  onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
                  showInPortal={false}
                  title={tooltipContent}
                >
                  <ReadyIcon className={classNames({ replay })} />
                </Tooltip>
              </ReadyIconWrapper>
            </TileWrapper>
          </ButtonBase>
        </OnboardingHighlightWrapper>
        <SeriesBoardOnboarding isReplay={!!replay} onClose={onClick} />
        {averageMode ? (
          <TrainingSetDialog
            task={task}
            open={openReplayIntroDialog}
            onClose={() => setOpenReplayIntroDialog(false)}
            to={to}
          />
        ) : (
          <ReplayIntroDialog
            task={task}
            open={openReplayIntroDialog}
            onClose={() => setOpenReplayIntroDialog(false)}
            to={to}
          />
        )}
      </>
    );
  }

  if (aasmState === ManagerProgressionTaskState.COMPLETED) {
    if (hasSharedPool && isParentTrackCancelled) {
      return (
        <Tooltip
          open
          placement="bottom"
          showInPortal={false}
          title={
            <FormattedMessage
              defaultMessage="Board completed — you're in the winners list! <link>Go to play</link> to take on another one."
              values={{
                link: (chunks: ReactNode) => (
                  <UnderlineLink
                    to={generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO)}
                  >
                    {chunks}
                  </UnderlineLink>
                ),
              }}
            />
          }
        >
          <div>
            <TileWrapper>
              <CheckmarkTile />
              <ArrowDownShapeSvg colors={{ start: '#00F34D', end: '#00D5FF' }}>
                <CroppedPlayerImage
                  style={{
                    background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                  }}
                />
              </ArrowDownShapeSvg>
            </TileWrapper>
          </div>
        </Tooltip>
      );
    }
    const kycState = getKycClaimState(shouldFillKycBeforeClaim, kycUnderReview);
    const kycPendingVerification = kycState === 'pendingVerification';
    const kycActionRequired = kycState === 'actionRequired';

    const getArrowColors = () => {
      if (kycPendingVerification) {
        return { start: '#000000', end: '#B5B5B5' };
      }
      if (kycActionRequired) {
        return { start: '#7A5A00', end: '#FFD914' };
      }
      return undefined;
    };

    const claimableTile = (
      <StyledButtonBase
        {...(replay ? { onClick: () => onClaimTask(id) } : { to: lineupsPath })}
      >
        <TileWrapper>
          <ClaimableTile
            gold={kycActionRequired}
            pending={kycPendingVerification}
          />
          <ArrowDownShapeSvg floating colors={getArrowColors()}>
            <Vertical gap={0} center>
              <RewardConfigImage anyRewardConfig={rewardConfigs[0]} />
              <BrandS>
                <RewardConfigText anyRewardConfig={rewardConfigs[0]} />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );

    if (!shouldFillKycBeforeClaim) {
      return claimableTile;
    }

    if (kycPendingVerification) {
      return (
        <ActionRequiredTile>
          {claimableTile}
          <ActionRequiredCtaWrapper>
            <PendingVerificationCta />
          </ActionRequiredCtaWrapper>
        </ActionRequiredTile>
      );
    }

    return (
      <>
        <ActionRequiredTile>
          {claimableTile}
          <ActionRequiredCtaWrapper>
            <ActionRequiredCta onClick={() => setOpenKycPromptDialog(true)} />
          </ActionRequiredCtaWrapper>
        </ActionRequiredTile>
        <JonumKycPromptDialog
          open={openKycPromptDialog}
          onDone={() => setOpenKycPromptDialog(false)}
        />
      </>
    );
  }

  if (aasmState === ManagerProgressionTaskState.CLAIMED) {
    return (
      <StyledButtonBase
        to={replay ? undefined : lineupsPath}
        as={replay ? 'div' : undefined}
      >
        <TileWrapper>
          <CheckmarkTile />
          <ArrowDownShapeSvg colors={{ start: '#00F34D', end: '#00D5FF' }}>
            <CroppedPlayerImage
              style={{
                background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
              }}
            />
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );
  }

  if (aasmState === ManagerProgressionTaskState.ASSIGNED) {
    return (
      <TileWrapper>
        <Tooltip
          open={!isReplay}
          placement="top"
          showInPortal={false}
          onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
          title={tooltipContent}
        >
          <img src={lockedTile} alt="locked tile" width={30} />
        </Tooltip>
      </TileWrapper>
    );
  }

  return null;
};

HotStreakTile.fragments = {
  thresholdPickerTask: gql`
    fragment HotStreakTile_thresholdPickerTask on ThresholdPickerTask {
      id
      aasmState
      replay
      averageMode
      rewardConfigs {
        id
        ...RewardConfigImage_anyRewardConfigInterface
        ...RewardConfigText_anyRewardConfigInterface
      }
      displayedPlayer {
        slug
        squaredPictureUrl
      }
      taskLineups {
        id
        aasmState
        hasLiveGames
      }
      parentTask {
        id
        ... on TasksTrack {
          id
          aasmState
        }
      }
      ...ReplayIntroDialog_taskInterface
      ...Lifes_thresholdPickerTask
      ...TaskTooltipContent_thresholdPickerTask
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${ReplayIntroDialog.fragments.taskInterface}
    ${TrainingSetDialog.fragments.taskInterface}
    ${Lifes.fragments.thresholdPickerTask}
    ${TaskTooltipContent.fragments.thresholdPickerTask}
  ` as TypedDocumentNode<HotStreakTile_thresholdPickerTask>,
};
